"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { sanitizeForDisplay } from "../../html"
import { cn } from "../../lib/utils"
import type { DisplayGap, DisplayInlineEmbed, DisplayTextEmbed } from "../../shared/types"

/**
 * Renders HTML content with proper MathML support
 * Uses innerHTML assignment to preserve MathML structure
 */
export function HTMLContent({
	html,
	className,
	inlineEmbeds,
	textEmbeds,
	gapEmbeds,
	renderInline,
	renderTextEntry,
	renderGap
}: {
	html: string
	className?: string
	inlineEmbeds?: Record<string, DisplayInlineEmbed>
	textEmbeds?: Record<string, DisplayTextEmbed>
	gapEmbeds?: Record<string, DisplayGap>
	renderInline?: (embed: DisplayInlineEmbed) => React.ReactElement
	renderTextEntry?: (embed: DisplayTextEmbed) => React.ReactElement
	renderGap?: (gap: DisplayGap) => React.ReactElement
}) {
	// Create a ref to handle MathML fallback if needed
	const containerRef = React.useRef<HTMLDivElement>(null)
	const [targets, setTargets] = React.useState<Record<string, Element>>({})

	// Sanitize incoming HTML to strip unsafe and layout-breaking attributes
	const safeHtml = React.useMemo(() => sanitizeForDisplay(html), [html])

	// Apply sanitized HTML to the container whenever it changes, and find targets
	React.useEffect(() => {
		const el = containerRef.current
		if (!el) return
		el.innerHTML = safeHtml

		const newTargets: Record<string, Element> = {}

		// Find inline dropdown placeholders
		if (inlineEmbeds) {
			const nodes = el.querySelectorAll("[data-qti-inline]")
			for (const node of nodes) {
				const responseId = node.getAttribute("data-qti-inline")
				if (responseId && inlineEmbeds[responseId]) {
					newTargets[responseId] = node
				}
			}
		}

		// Find text entry placeholders
		if (textEmbeds) {
			const nodes = el.querySelectorAll("[data-qti-text-entry]")
			for (const node of nodes) {
				const responseId = node.getAttribute("data-qti-text-entry")
				if (responseId && textEmbeds[responseId]) {
					newTargets[responseId] = node
				}
			}
		}

		// Find gap placeholders (for gap match interactions)
		if (gapEmbeds) {
			const nodes = el.querySelectorAll("[data-qti-gap]")
			for (const node of nodes) {
				const gapId = node.getAttribute("data-qti-gap")
				if (gapId && gapEmbeds[gapId]) {
					newTargets[`gap-${gapId}`] = node
				}
			}
		}

		setTargets(newTargets)
	}, [safeHtml, inlineEmbeds, textEmbeds, gapEmbeds]) // Re-scan if structure changes

	// Render portals declaratively based on targets
	// This ensures React reconciles them properly even if renderTextEntry changes identity
	const portals = React.useMemo(() => {
		const items: React.ReactNode[] = []

		if (inlineEmbeds && renderInline) {
			for (const [id, embed] of Object.entries(inlineEmbeds)) {
				const target = targets[id]
				if (target) {
					items.push(createPortal(renderInline(embed), target, id))
				}
			}
		}

		if (textEmbeds && renderTextEntry) {
			for (const [id, embed] of Object.entries(textEmbeds)) {
				const target = targets[id]
				if (target) {
					items.push(createPortal(renderTextEntry(embed), target, id))
				}
			}
		}

		if (gapEmbeds && renderGap) {
			for (const [id, gap] of Object.entries(gapEmbeds)) {
				const target = targets[`gap-${id}`]
				if (target) {
					items.push(createPortal(renderGap(gap), target, `gap-${id}`))
				}
			}
		}

		return items
	}, [targets, inlineEmbeds, textEmbeds, gapEmbeds, renderInline, renderTextEntry, renderGap])

	React.useEffect(() => {
		// Check if MathML is supported
		const checkMathMLSupport = () => {
			const mml = document.createElement("math")
			mml.innerHTML = "<mspace/>"
			const firstChild = mml.firstChild
			return firstChild instanceof Element && firstChild.namespaceURI === "http://www.w3.org/1998/Math/MathML"
		}

		// If MathML is not supported, we could add fallback logic here
		if (!checkMathMLSupport() && containerRef.current?.querySelector("math")) {
			// Intentionally quiet: sanitized HTML is still rendered
		}
	}, [])

	return (
		<>
			<div
				ref={containerRef}
				className={cn(
					"qti-html-content text-foreground text-lg font-medium leading-relaxed",
					// Ensure images never stretch or overflow
					"[&_img]:max-w-full [&_img]:h-auto [&_img]:w-auto [&_img]:object-contain [&_img]:block [&_img]:mx-auto",
					className
				)}
			/>
			{portals}
		</>
	)
}

export default HTMLContent
